import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/Modal';
import AddSellTransactionForm, { SellTransactionFormData } from './AddSellTransactionForm';
import { 
  getSellListAPI, 
  saveSellAPI, 
  deleteSellAPI,
  SellResponse,
  SellSearch,
  SellRequest,
  getPartyListAPI,
  getTokenListAPI,
  Party,
  Token
} from '../../../../services/adminApi';
import { ToastContainer } from '../../../../components/Toast';
import { useToast } from '../../../../hooks/useToast';
import { useModulePermission } from '../../../../hooks/useModulePermission';
import styles from './Transactions.module.css';

const Sell: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [transactions, setTransactions] = useState<SellResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTransaction, setEditingTransaction] = useState<SellResponse | null>(null);
  const [parties, setParties] = useState<any[]>([]);
  const [tokens, setTokens] = useState<any[]>([]);
  const today = new Date().toISOString().split('T')[0];
  const [filterData, setFilterData] = useState({
    fromDate: today,
    toDate: today,
    party: '',
    token: '',
    payIn: '',
  });
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const permissions = useModulePermission('Sell');

  // Load transactions on component mount and when filters change
  useEffect(() => {
    loadTransactions();
    loadFilterData();
  }, [filterData, searchQuery]);

  const loadFilterData = async () => {
    try {
      const [partiesResponse, tokensResponse] = await Promise.all([
        getPartyListAPI({ pageNo: 1, pageSize: 100 }),
        getTokenListAPI({ pageNo: 1, pageSize: 100 }),
      ]);
      setParties((partiesResponse.data || []).filter(p => p.isActive));
      setTokens((tokensResponse.data || []).filter(t => t.isActive));
    } catch (error) {
      console.error('Failed to load filter data:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const searchRequest: SellSearch = {
        partyId:0,
        tokenId:0,
        paymentInId:0,
        searchText:'',
        pageNo: 1,
        pageSize: 100,
      };
      
      // Add filter fields only if they have values
      if (filterData.fromDate) searchRequest.fromDate = filterData.fromDate;
      if (filterData.toDate) searchRequest.toDate = filterData.toDate;
      if (filterData.party) searchRequest.partyId = parseInt(filterData.party);
      if (filterData.token) searchRequest.tokenId = parseInt(filterData.token);
      if (filterData.payIn) {
        const payInMap: { [key: string]: number } = {
          'INR': 1,
          'USD': 2,
          'Token': 3
        };
        searchRequest.paymentInId = payInMap[filterData.payIn];
      }
      if (searchQuery) searchRequest.searchText = searchQuery;
      
      const response = await getSellListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setTransactions(response.data);
      } else {
        showError(response.message || 'Failed to load sell transactions');
      }
    } catch (error) {
      console.error('Failed to load sell transactions:', error);
      showError('Failed to load sell transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTransaction(null);
  };

  const handleSubmitTransaction = async (transactionData: SellTransactionFormData) => {
    try {
      const sellRequest: SellRequest = {
        id: editingTransaction?.id,
        partyId: parseInt(transactionData.partyId) || undefined,
        sellDate: transactionData.date,
        paymentInId: transactionData.payIn === 'INR' ? 1 : transactionData.payIn === 'USD' ? 2 : 3,
        tokenId: parseInt(transactionData.tokenId) || undefined,
        sellQty: parseFloat(transactionData.quantity.toString()) || 0,
        tokenToDollarRate: parseFloat(transactionData.tokenRate.toString()) || 0,
        dollarToINRRate: parseFloat(transactionData.dollarRate.toString()) || 0,
        totalAmtInINR: parseFloat(transactionData.total.toString()) || 0,
        paymentModeId: parseInt(transactionData.paymentModeId) || undefined,
        fromAccountId: parseInt(transactionData.fromAccountId) || undefined,
        payToAccountId: parseInt(transactionData.partyAccountId) || undefined,
        referenceNo: transactionData.referenceNo,
        paymentDate: transactionData.paymentDate,
        remarks: transactionData.remarks,
      };

      const response = await saveSellAPI(sellRequest);
      
      if (response.isSuccess) {
        showSuccess(editingTransaction ? 'Sell transaction updated successfully!' : 'Sell transaction created successfully!');
        await loadTransactions();
        setIsModalOpen(false);
        setEditingTransaction(null);
      } else {
        showError(response.message || 'Failed to save transaction');
      }
    } catch (error) {
      console.error('Failed to save sell transaction:', error);
      showError('Failed to save transaction. Please try again.');
    }
  };

  const handleEdit = (transaction: SellResponse) => {
    setEditingTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleDelete = async (transaction: SellResponse) => {
    if (window.confirm(`Are you sure you want to delete this transaction?`)) {
      try {
        if (!transaction.id) {
          showError('Transaction ID is missing');
          return;
        }
        const response = await deleteSellAPI(transaction.id);
        if (response.isSuccess) {
          showSuccess('Sell transaction deleted successfully!');
          await loadTransactions();
        } else {
          showError(response.message || 'Failed to delete transaction');
        }
      } catch (error) {
        console.error('Failed to delete transaction:', error);
        showError('Failed to delete transaction. Please try again.');
      }
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilterData({
      fromDate: today,
      toDate: today,
      party: '',
      token: '',
      payIn: '',
    });
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    showSuccess('Export functionality coming soon!');
  };

  // Filter transactions based on search
  const filteredTransactions = transactions.filter((transaction) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (transaction.partyName?.toLowerCase().includes(searchLower) || false) ||
      (transaction.tokenName?.toLowerCase().includes(searchLower) || false) ||
      (transaction.paymentIn?.toLowerCase().includes(searchLower) || false) ||
      (transaction.fromAccountName?.toLowerCase().includes(searchLower) || false) ||
      (transaction.paymentModeName?.toLowerCase().includes(searchLower) || false)
    );
  });

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Sell Transactions</h1>
          <p className={styles.subtitle}>Manage sell transactions</p>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.filterButton} 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            title="Filter"
          >
            🔽 Filters
          </button>
          {permissions.canExport && (
            <button 
              className={styles.exportButton} 
              onClick={handleExport}
              title="Export"
            >
              📥 Export
            </button>
          )}
          {permissions.canAdd && (
            <button className={styles.addButton} onClick={handleAddTransaction}>
              + Add Sell Transaction
            </button>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {isFilterOpen && (
        <div className={styles.filterPanel}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label>FROM DATE</label>
              <input
                type="date"
                name="fromDate"
                value={filterData.fromDate}
                onChange={handleFilterChange}
                className={styles.filterInput}
              />
            </div>
            <div className={styles.filterGroup}>
              <label>TO DATE</label>
              <input
                type="date"
                name="toDate"
                value={filterData.toDate}
                onChange={handleFilterChange}
                className={styles.filterInput}
              />
            </div>
            <div className={styles.filterGroup}>
              <label>PARTY</label>
              <select
                name="party"
                value={filterData.party}
                onChange={handleFilterChange}
                className={styles.filterSelect}
              >
                <option value="">All Parties</option>
                {parties.map((party) => (
                  <option key={party.id} value={party.id}>
                    {party.partyName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>TOKEN</label>
              <select
                name="token"
                value={filterData.token}
                onChange={handleFilterChange}
                className={styles.filterSelect}
              >
                <option value="">All Tokens</option>
                {tokens.map((token) => (
                  <option key={token.id} value={token.id}>
                    {token.tokenName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>PAY IN</label>
              <select
                name="payIn"
                value={filterData.payIn}
                onChange={handleFilterChange}
                className={styles.filterSelect}
              >
                <option value="">All</option>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="Token">Token</option>
              </select>
            </div>
          </div>
          <div className={styles.filterActions}>
            <button 
              className={styles.clearButton}
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading transactions...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>DATE</th>
                <th>PARTY</th>
                <th>TOKEN</th>
                <th>PAY IN</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>OUR ACCOUNT</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 8 : 7} className={styles.emptyState}>No transactions found</td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.sellDate ? new Date(transaction.sellDate).toLocaleDateString('en-IN') : '-'}</td>
                    <td>{transaction.partyName || '-'}</td>
                    <td>{transaction.tokenName || '-'}</td>
                    <td>{transaction.paymentIn || '-'}</td>
                    <td>{transaction.sellQty || 0}</td>
                    <td>₹{(transaction.totalAmtInINR || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{transaction.fromAccountName || '-'}</td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(transaction)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(transaction)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Transaction Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingTransaction ? 'Edit Sell Transaction' : 'Add Sell Transaction'}
      >
        <AddSellTransactionForm
          onSubmit={handleSubmitTransaction}
          onCancel={handleCloseModal}
          initialData={editingTransaction ? {
            date: editingTransaction.sellDate ? editingTransaction.sellDate.split('T')[0] : new Date().toISOString().split('T')[0],
            partyId: editingTransaction.partyId?.toString() || '',
            partyName: editingTransaction.partyName || '',
            mobile: '',
            address: '',
            tokenId: editingTransaction.tokenId?.toString() || '',
            tokenName: editingTransaction.tokenName || '',
            payIn: editingTransaction.paymentIn || 'INR',
            quantity: editingTransaction.sellQty || 0,
            tokenRate: editingTransaction.tokenToDollarRate || 1,
            dollarRate: editingTransaction.dollarToINRRate || 83,
            total: editingTransaction.totalAmtInINR || 0,
            paymentModeId: editingTransaction.paymentModeId?.toString() || '',
            paymentMode: editingTransaction.paymentModeName || '',
            fromAccountId: editingTransaction.fromAccountId?.toString() || '',
            fromAccount: editingTransaction.fromAccountName || '',
            partyAccountId: editingTransaction.payToAccountId?.toString() || '',
            partyAccount: editingTransaction.payToAccountName || '',
            referenceNo: editingTransaction.referenceNo || '',
            paymentDate: editingTransaction.paymentDate ? editingTransaction.paymentDate.split('T')[0] : new Date().toISOString().split('T')[0],
            remarks: editingTransaction.remarks || '',
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default Sell;
